/**
 * Simple Product Carousel - Clean Implementation
 */

class CarouselJava {
  constructor() {
    this.carousel = document.querySelector('[data-carousel-java]');
    if (!this.carousel) return;

    this.track = this.carousel.querySelector('.carousel--java__track');
    this.slides = this.carousel.querySelector('.carousel--java__slides');
    this.prevBtn = this.carousel.querySelector('.carousel--java__arrow--prev');
    this.nextBtn = this.carousel.querySelector('.carousel--java__arrow--next');
    this.dotsContainer = this.carousel.querySelector('.carousel--java__dots');
    this.dots = this.carousel.querySelectorAll('.carousel--java__dot');

    this.currentIndex = 0;
    this.visibleSlides = 4;
    this.totalSlides = 0;
    this.slideWidth = 0;
    this.gap = 0;

    this.init();
  }

  init() {
    this.setupSlides();
    this.determineVisibleSlides();
    this.calculateSlideWidth();
    this.buildDots();
    this.setupEventListeners();
    this.updateCarousel();
  }

  setupSlides() {
    const realSlides = this.slides.querySelectorAll('.carousel--java__slide:not(.carousel--java__slide--skeleton)');
    this.totalSlides = realSlides.length;
    
    if (this.totalSlides === 0) return;

    // Calculate slide width
    this.calculateSlideWidth();
  }

  calculateSlideWidth() {
    const firstSlide = this.slides.querySelector('.carousel--java__slide');
    if (firstSlide) {
      this.slideWidth = firstSlide.getBoundingClientRect().width;
    }
    // Read actual gap from computed styles (supports browsers where column-gap/gap is reported)
    const styles = window.getComputedStyle(this.slides);
    const colGap = parseFloat(styles.getPropertyValue('column-gap'));
    const rowGap = parseFloat(styles.getPropertyValue('gap'));
    const computedGap = !isNaN(colGap) && colGap > 0 ? colGap : (!isNaN(rowGap) ? rowGap : 0);
    this.gap = computedGap || 0;
  }

  setupEventListeners() {
    if (this.prevBtn) {
      this.prevBtn.addEventListener('click', () => this.prev());
    }
    
    if (this.nextBtn) {
      this.nextBtn.addEventListener('click', () => this.next());
    }

    this.attachDotHandlers();

    window.addEventListener('resize', () => {
      const prevVisible = this.visibleSlides;
      this.determineVisibleSlides();
      this.calculateSlideWidth();
      const maxIndex = Math.max(0, this.totalSlides - this.visibleSlides);
      if (this.currentIndex > maxIndex) {
        this.currentIndex = maxIndex;
      }
      if (this.visibleSlides !== prevVisible) {
        this.buildDots();
      }
      this.updateCarousel();
    });
  }

  determineVisibleSlides() {
    const width = window.innerWidth || document.documentElement.clientWidth;
    if (width <= 768) {
      this.visibleSlides = 2;
    } else if (width <= 1024) {
      this.visibleSlides = 3;
    } else {
      this.visibleSlides = 4;
    }
  }

  prev() {
    if (this.currentIndex > 0) {
      this.currentIndex--;
      this.updateCarousel();
    }
  }

  next() {
    const maxIndex = Math.max(0, this.totalSlides - this.visibleSlides);
    if (this.currentIndex < maxIndex) {
      this.currentIndex++;
      this.updateCarousel();
    }
  }

  goToSlide(index) {
    const maxIndex = Math.max(0, this.totalSlides - this.visibleSlides);
    this.currentIndex = Math.min(index, maxIndex);
    this.updateCarousel();
  }

  updateCarousel() {
    if (this.totalSlides === 0) return;

    // Move slides
    const translateX = -this.currentIndex * (this.slideWidth + this.gap);
    this.slides.style.transform = `translateX(${translateX}px)`;

    // Update arrows
    this.updateArrows();
    
    // Update dots
    this.updateDots();
  }

  updateArrows() {
    if (!this.prevBtn || !this.nextBtn) return;

    const maxIndex = Math.max(0, this.totalSlides - this.visibleSlides);
    
    this.prevBtn.disabled = this.currentIndex === 0;
    this.nextBtn.disabled = this.currentIndex >= maxIndex;
  }

  updateDots() {
    this.dots.forEach((dot, index) => {
      const isActive = index === this.currentIndex;
      dot.classList.toggle('carousel--java__dot--active', isActive);
      dot.setAttribute('aria-selected', isActive ? 'true' : 'false');
    });
  }

  buildDots() {
    if (!this.dotsContainer) return;
    if (this.totalSlides === 0) return;
    const maxPositions = Math.max(1, this.totalSlides - this.visibleSlides + 1);
    const currentClamped = Math.min(this.currentIndex, maxPositions - 1);
    this.currentIndex = currentClamped;
    const buttons = [];
    for (let i = 0; i < maxPositions; i++) {
      const isActive = i === this.currentIndex;
      buttons.push(`<button class="carousel--java__dot${isActive ? ' carousel--java__dot--active' : ''}" role="tab" aria-label="Go to position ${i + 1}" aria-selected="${isActive ? 'true' : 'false'}" data-slide="${i}"></button>`);
    }
    this.dotsContainer.innerHTML = buttons.join('');
    this.dots = this.carousel.querySelectorAll('.carousel--java__dot');
    this.attachDotHandlers();
  }

  attachDotHandlers() {
    this.dots.forEach((dot) => {
      dot.addEventListener('click', () => {
        const idx = parseInt(dot.getAttribute('data-slide') || '0', 10);
        this.goToSlide(idx);
      });
    });
  }
}

// Initialize
document.addEventListener('DOMContentLoaded', () => {
  new CarouselJava();
});